import { useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { currentUserActions } from "../reducers/currentUser";

interface UseAppWebSocketOptions {
  groupPath: string | null | undefined;
  onMessage: (rawData: any) => void;
  pingIntervalMs?: number;
  pongTimeoutMs?: number;
  reconnectDelayMs?: number;
}

const getSocketGroup = (ws: any): string | null => {
  if (!ws) return null;
  if (ws._groupPath) return ws._groupPath;
  if (ws.url) {
    try {
      const match = ws.url.match(/\/join-group\/([^/?#]+)/);
      if (match && match[1]) return match[1];
    } catch (e) {
      // ignore url parsing error
    }
  }
  return null;
};

export default function useAppWebSocket({
  groupPath,
  onMessage,
  pingIntervalMs = 15000,
  pongTimeoutMs = 5000,
  reconnectDelayMs = 3000,
}: UseAppWebSocketOptions) {
  const dispatch = useDispatch();
  const socketInstance = useSelector((state: any) => state.currentUser.socketInstance);

  const onMessageRef = useRef(onMessage);
  const groupPathRef = useRef(groupPath);
  const socketInstanceRef = useRef(socketInstance);
  const reconnectTimeoutRef = useRef<any>(null);
  const isExplicitCloseRef = useRef(false);

  onMessageRef.current = onMessage;
  groupPathRef.current = groupPath;
  socketInstanceRef.current = socketInstance;

  const clearSocketTimers = (ws: any) => {
    if (!ws) return;
    if (ws._pingInterval) {
      clearInterval(ws._pingInterval);
      ws._pingInterval = null;
    }
    if (ws._pongTimeout) {
      clearTimeout(ws._pongTimeout);
      ws._pongTimeout = null;
    }
  };

  const onSocketMessage = (event: MessageEvent, ws: WebSocket) => {
    if (ws && (ws as any)._pongTimeout) {
      clearTimeout((ws as any)._pongTimeout);
      (ws as any)._pongTimeout = null;
    }

    try {
      const data = JSON.parse(event?.data);
      if (data?.type === "pong") {
        return;
      }
    } catch (e) {
      // ignore non-JSON messages
      return;
    }

    onMessageRef.current(event?.data);
  };

  const connectSocket = useCallback(() => {
    if (isExplicitCloseRef.current) return;
    const currentGroup = groupPathRef.current;
    if (!currentGroup) return;

    const currentSocket = socketInstanceRef.current;
    if (
      getSocketGroup(currentSocket) === currentGroup &&
      currentSocket &&
      (currentSocket.readyState === WebSocket.OPEN ||
        currentSocket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    if (currentSocket) {
      clearSocketTimers(currentSocket);
      currentSocket.onopen = null;
      currentSocket.onmessage = null;
      currentSocket.onerror = null;
      currentSocket.onclose = null;
      currentSocket.close();
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const protocol = window.location.protocol === "http:" ? "ws://" : "wss://";
    const url = `${protocol}${window.location.host}/join-group/${currentGroup}`;
    const newSocket = new WebSocket(url);
    (newSocket as any)._groupPath = currentGroup;
    socketInstanceRef.current = newSocket;

    newSocket.onmessage = (event: MessageEvent) => {
      onSocketMessage(event, newSocket);
    };

    newSocket.onclose = () => {
      clearSocketTimers(newSocket);
      if (socketInstanceRef.current === newSocket && !isExplicitCloseRef.current) {
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          connectSocket();
        }, reconnectDelayMs);
      }
    };

    newSocket.onerror = () => {
      clearSocketTimers(newSocket);
      newSocket.close();
    };

    newSocket.onopen = () => {
      (newSocket as any)._pingInterval = setInterval(() => {
        if (newSocket.readyState !== WebSocket.OPEN) {
          clearSocketTimers(newSocket);
          newSocket.close();
        } else {
          newSocket.send(JSON.stringify({ type: "ping" }));
          if ((newSocket as any)._pongTimeout) {
            clearTimeout((newSocket as any)._pongTimeout);
          }
          (newSocket as any)._pongTimeout = setTimeout(() => {
            console.warn("[WebSocket] Pong timeout (no response from server). Reconnecting...");
            clearSocketTimers(newSocket);
            newSocket.close();
          }, pongTimeoutMs);
        }
      }, pingIntervalMs);
    };

    dispatch(currentUserActions.setSocketInstance(newSocket));
  }, [dispatch, pingIntervalMs, pongTimeoutMs, reconnectDelayMs]);

  const disconnect = useCallback(() => {
    isExplicitCloseRef.current = true;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    const currentSocket = socketInstanceRef.current;
    if (currentSocket) {
      clearSocketTimers(currentSocket);
      currentSocket.onopen = null;
      currentSocket.onmessage = null;
      currentSocket.onerror = null;
      currentSocket.onclose = null;
      currentSocket.close();
      socketInstanceRef.current = null;
    }
    dispatch(currentUserActions.setSocketInstance(null));
  }, [dispatch]);

  useEffect(() => {
    if (!groupPath) {
      if (socketInstanceRef.current) {
        disconnect();
      }
      return;
    }

    isExplicitCloseRef.current = false;

    const currentSocket = socketInstanceRef.current;
    const currentGroup = getSocketGroup(currentSocket);
    const isDifferentGroup = currentGroup !== groupPath;
    const isSocketDead =
      !currentSocket ||
      currentSocket.readyState === WebSocket.CLOSED ||
      currentSocket.readyState === WebSocket.CLOSING;

    if (isDifferentGroup || isSocketDead) {
      connectSocket();
      return;
    }

    socketInstance.onmessage = (event: MessageEvent) => {
      onSocketMessage(event, socketInstance);
    };
  }, [socketInstance, groupPath, connectSocket, disconnect]);

  useEffect(() => {
    const handleVisibilityOrOnline = () => {
      if (document.visibilityState === "visible" || navigator.onLine) {
        const currentSocket = socketInstanceRef.current;
        if (
          !currentSocket ||
          currentSocket.readyState === WebSocket.CLOSED ||
          currentSocket.readyState === WebSocket.CLOSING
        ) {
          connectSocket();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityOrOnline);
    window.addEventListener("online", handleVisibilityOrOnline);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityOrOnline);
      window.removeEventListener("online", handleVisibilityOrOnline);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [connectSocket]);

  return { disconnect };
}
